import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { API } from "aws-amplify";
import { useReactToPrint } from 'react-to-print';

import Fuse from 'fuse.js';
import swal from '@sweetalert/with-react';

import Dropdown from '../Dropdown';
import ScheduleGrid from './ScheduleGrid';
import ScheduleGridItem from './ScheduleGridItem';
import ScheduleGridOverlayItem from './ScheduleGridOverlayItem';

import EditBlock from './modals/edit_block';
import EditTeacherData from './modals/edit_teacher';
import EditClassroomData from './modals/edit_classroom';
import EditStudentData from './modals/edit_student';

import { getJSONFromStorage, getData, formatTime, useOutsideClick, sortArrayOfObjects, useDebouncedValue } from '../../js/Helpers';
import { buildDaySchedule, buildDayOverlays, calculateTimeGridColumnStartEnd, getTimeGridConflictKeys, timesOverlap } from './utils/scheduleGridUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlusCircle, faSearch, faTimes, faChevronDown, faChevronUp, faChalkboard, faUserGraduate, faEdit, faChevronRight, faLock, faCalendarDay, faChalkboardTeacher, faGraduationCap, faSlidersH, faCheckSquare, faSquare, faCircleExclamation, faUnlock} from '@fortawesome/free-solid-svg-icons';

import '../../css/magnetboard.css';

const MagnetboardSearchInput = React.memo(function MagnetboardSearchInput({ value = '', onSearchChange = () => {}, placeholder = '' }) {
	const [inputValue, setInputValue] = useState(value);
	const debouncedInputValue = useDebouncedValue(inputValue, 80);

	useEffect(() => {
		setInputValue(value);
	}, [value]);

	useEffect(() => {
		onSearchChange(debouncedInputValue);
	}, [debouncedInputValue, onSearchChange]);

	return (
		<input
			id='mb-search-bar'
			className='mb-search-bar'
			placeholder={placeholder}
			value={inputValue}
			onChange={(e) => setInputValue(e.target.value)}
		/>
	);
});

export default function MagnetboardElementary({ schoolSubscribed, schoolInfoLoading, schoolInfo, scheduleInfo, scheduleInfoLoading, periodsLoading, periods, teachersLoading, teachers, setTeachers, classroomsLoading, classrooms, setClassrooms, studentsLoading, students, setStudents, coursesLoading, courses, setCourses, blocksLoading, blocks, setBlocks, magnetboardOptions = {show_top_bar:true, allow_filtering:true, show_conflicts:true, show_menu:true, allowed_teacher_ids:null, allowed_classroom_ids:null, view_options:[{value:'teacher', display:'Teacher View'}, {value:'classroom', display:'Classroom View'}, {value:'student', display:'Student View'}]} }) {

	/////////////////////////////
	///// LOADING VARIABLES /////
	/////////////////////////////
	const [isLoading, setIsLoading] = useState(true);

	//////////////////////////
	///// DATA VARIABLES /////
	//////////////////////////
	const [blockData, setBlockData] = useState(null);
	const [currentView, setCurrentView] = useState('teacher');
	const [displayData, setDisplayData] = useState([]);
	const [openDisplayData, setOpenDisplayData] = useState([]);
	const [searchValue, setSearchValue] = useState('');

	const [filterDataVisible, setFilterDataVisible] = useState(false);
	const [filterOptions, setFilterOptions] = useState({});
	const [openFilterOptions, setOpenFilterOptions] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [filterIDs, setFilterIDs] = useState([]);

	const [sidePanelClosing, setSidePanelClosing] = useState(false);
	const [displayCreated, setDisplayCreated] = useState(false);
	const [deletingBlockTargets, setDeletingBlockTargets] = useState([]);

	const [conflicts] = useState([]);
	const [openConflicts, setOpenConflicts] = useState([]);

	const [dataEditingID, setDataEditingID] = useState(null);
	const [showEditDataModal, setShowEditDataModal] = useState(false);

	const [currentOpenMenu, setCurrentOpenMenu] = useState(null);

	const [maxTeachersToShow, setMaxTeachersToShow] = useState(10);
	const [maxStudentsToShow, setMaxStudentsToShow] = useState(50);
	const [days, setDays] = useState([]);
	const displayDependenciesLoading = scheduleInfoLoading || blocksLoading || teachersLoading || coursesLoading || classroomsLoading || (currentView === 'student' && studentsLoading);

	const searchableTeachers = useMemo(() => teachers.map((teacher) => ({
		...teacher,
		search_display_name: `${teacher.name || ''}, ${teacher.first_name || ''}`.trim(),
		search_full_name: `${teacher.first_name || ''} ${teacher.name || ''}`.trim(),
		search_reverse_name: `${teacher.name || ''} ${teacher.first_name || ''}`.trim()
	})), [teachers]);

	const searchableStudents = useMemo(() => students.map((student) => ({
		...student,
		search_display_name: `${student.last_name || ''}, ${student.first_name || ''}`.trim(),
		search_full_name: `${student.first_name || ''} ${student.last_name || ''}`.trim(),
		search_reverse_name: `${student.last_name || ''} ${student.first_name || ''}`.trim()
	})), [students]);

	const teacherSearchFuse = useMemo(() => new Fuse(searchableTeachers, {
		keys: ['name', 'first_name', 'search_display_name', 'search_full_name', 'search_reverse_name'],
		threshold: .22,
		ignoreLocation: true
	}), [searchableTeachers]);

	const classroomSearchFuse = useMemo(() => new Fuse(classrooms, {
		keys: ['classroom_name', 'classroom_type'],
		threshold: .1
	}), [classrooms]);

	const studentSearchFuse = useMemo(() => new Fuse(searchableStudents, {
		keys: ['first_name', 'last_name', 'search_display_name', 'search_full_name', 'search_reverse_name'],
		threshold: .22,
		ignoreLocation: true
	}), [searchableStudents]);

	const teacherById = useMemo(() => {
		const nextMap = new Map();
		teachers.forEach((teacher) => {
			nextMap.set(`${teacher.teacher_id}`, teacher);
		});
		return nextMap;
	}, [teachers]);

	const classroomById = useMemo(() => {
		const nextMap = new Map();
		classrooms.forEach((classroom) => {
			nextMap.set(`${classroom.classroom_id}`, classroom);
		});
		return nextMap;
	}, [classrooms]);

	const courseById = useMemo(() => {
		const nextMap = new Map();
		courses.forEach((course) => {
			nextMap.set(`${course.course_id}`, course);
		});
		return nextMap;
	}, [courses]);

	const homeroomStudentsByTeacherId = useMemo(() => {
		const nextMap = new Map();
		students.forEach((student) => {
			const teacherId = `${student.homeroom_teacher_id || ''}`;
			if(!teacherId) return;
			if(!nextMap.has(teacherId)) nextMap.set(teacherId, []);
			nextMap.get(teacherId).push(student.student_id);
		});
		return nextMap;
	}, [students]);

	const filterIdSet = useMemo(() => new Set(filterIDs.map((id) => `${id}`)), [filterIDs]);
	const filteredDataSet = useMemo(() => new Set(filteredData.map((id) => `${id}`)), [filteredData]);

	const conflictsByView = useMemo(() => {
		const teacherConflicts = new Map();
		const classroomConflicts = new Map();
		const studentConflicts = new Map();

		conflicts.forEach((conflict) => {
			(conflict.teachers || []).forEach((teacherId) => {
				const key = `${teacherId}`;
				if(!teacherConflicts.has(key)) teacherConflicts.set(key, []);
				teacherConflicts.get(key).push(conflict);
			});

			if(conflict.classroom_id)
			{
				const classroomKey = `${conflict.classroom_id}`;
				if(!classroomConflicts.has(classroomKey)) classroomConflicts.set(classroomKey, []);
				classroomConflicts.get(classroomKey).push(conflict);
			}

			(conflict.students || []).forEach((studentId) => {
				const key = `${studentId}`;
				if(!studentConflicts.has(key)) studentConflicts.set(key, []);
				studentConflicts.get(key).push(conflict);
			});
		});

		return { teacherConflicts, classroomConflicts, studentConflicts };
	}, [conflicts]);

	///////////////////
	///// USE REF /////
	///////////////////
	const menu_ref = useRef();
	const filter_ref = useRef();
	const printRef = useRef();

	useOutsideClick(menu_ref, () => {
		setCurrentOpenMenu(null);
	});

	useOutsideClick(filter_ref, () => {
		setFilterDataVisible(false);
	});

	useEffect(() => {
		if(filterDataVisible) return;
		setOpenFilterOptions([]);
	}, [filterDataVisible]);

	//////////////////////////
	///// MENU FUNCTIONS /////
	//////////////////////////
	const toggleMenuItem = (menu_item) => {
		let current_menu_item = null;

		if(menu_item !== currentOpenMenu) current_menu_item = menu_item;

		setCurrentOpenMenu(current_menu_item);
	}

	const toggleFilterOptions = () => {
		setFilterDataVisible(!filterDataVisible);
	}
	
	const updateFilterOptions = (filter_title, option_index, selected) => {
		filterOptions[filter_title][option_index]['selected'] = selected;
		setFilterOptions({...filterOptions});
	}

	const updateOpenFilters = (filter_title) => {
		const filter_index = openFilterOptions.findIndex(filter => filter === filter_title);
		
		if(filter_index !== -1)
		{
			openFilterOptions.splice(filter_index, 1);
		}
		else
		{
			openFilterOptions.push(filter_title);
		}

		setOpenFilterOptions([...openFilterOptions]);
	}

	// Handle Dropdown Hovering 
	const handleDropdownHoverShow = (action_type) => {
		// Hide all other submenus
		const all_submenus = document.querySelectorAll('.mb-dropdown-options-subcontainer');
		for(var i = 0; i < all_submenus.length; i++)
		{
			all_submenus[i].classList.add('hide');
		}

		// Show the current submenu
		const parent_menu = document.querySelector('.mb-dropdown-option-hover[data-actiontype="' + action_type + '"]');
		const parent_menu_top_position = parent_menu.offsetTop + 42;

		const submenu = document.querySelector('.mb-dropdown-options-subcontainer[data-actiontype="' + action_type + '"]');
		if(submenu) 
		{
			submenu.style.top = parent_menu_top_position + "px";
			submenu.classList.remove("hide");
		}
	}

	const handleDropdownHoverHide = (action_type) => {
		const submenu = document.querySelector('.mb-dropdown-options-subcontainer[data-actiontype="' + action_type + '"]');

		if(submenu) submenu.classList.add("hide");
	}

	const openPrint = useReactToPrint({
		content: () => printRef.current,
		pageStyle: `
			@media print {
				@page { 
					size: 11in 8.5in; 
					margin: 20mm;
				}

				html, body {
					height: initial !important;
					overflow: initial !important;
					-webkit-print-color-adjust: exact;
				}

				.mb-elem-teacher-schedule {
					margin:0px 0px 0px 0px !important;
				}

				.mb-elem-week-schedule-container {
					margin:0px 0px 0px 0px !important;
				}

				.mb-elem-week-day-header {
					margin:0px !important;
					font-size:12px !important;
				}
				
				.mb-hide-during-print {
					display:none !important;
					height:0 !important;
				}

				.page-break {
					margin-top: 1rem;
					display: block;
					page-break-before: always;
				}
			}
		`,
	});
	
	/////////////////////////////////
	///// MAGNETBOARD FUNCTIONS /////
	/////////////////////////////////
	const createDisplaySchedule = useCallback(() => {
		setIsLoading(true);

		// Organize schedules for display
		let display_schedule = [];
		let filter_options = {};
		
		if(currentView === 'teacher')
		{
			const sorted_teachers = sortArrayOfObjects(teachers, 'name', 'text', 'asc');
			filter_options = {
				'teachers':[], 
				'type':[
					{display:"Homeroom Teacher", value:"is_homeroom"}, 
					{display:"Specials Teacher", value:"is_specials"}, 
					{display:"Pullout Teacher", value:"is_pullout"}
				]};

			for(const teacher of sorted_teachers)
			{
				const teacher_id = teacher.teacher_id;
				const teacher_last_name = teacher.name;
				const teacher_first_name= teacher.first_name;
				const teacher_display_name = `${teacher_last_name}, ${teacher_first_name}`;

				filter_options['teachers'].push({display:teacher_display_name, value:teacher_id});

				display_schedule.push({data_id:teacher_id, display_name:teacher_display_name, display_info:teacher});
			}
		}
		else if(currentView === 'classroom')
		{
			const sorted_classrooms = sortArrayOfObjects(classrooms, 'classroom_name', 'text', 'asc');

			filter_options = {
				'classrooms':[]
			};

			for(const classroom of sorted_classrooms)
			{
				const classroom_id = classroom.classroom_id;
				const classroom_name = classroom.classroom_name;

				filter_options['classrooms'].push({display:classroom_name, value:classroom_id});
				display_schedule.push({data_id:classroom_id, display_name:classroom_name, display_info:classroom});
			}
		}
		else if(currentView === 'student')
		{
			filter_options = {
				'grades':[], 
				'homerooms': []
			}

			const grades = students.reduce((result, student) => {
				const student_grade = student.grade;
				if(student_grade && result.findIndex(option => option.value === student_grade) === -1) result.push({display:student_grade, value:student_grade, selected:false});
				return result;
			}, []);
			const sorted_grades = sortArrayOfObjects(grades, 'value', 'number', 'asc');

			const homeroom_teachers = teachers.reduce((result, teacher) => {
				const teacher_id = teacher.teacher_id;
				const teacher_first_name = teacher.first_name;
				const teacher_last_name = teacher.name;
				const teacher_name = `${teacher_last_name}, ${teacher_first_name}`;
				const is_homeroom_teacher = teacher.is_homeroom_teacher;
				if(is_homeroom_teacher === '1') result.push({display:teacher_name, value:teacher_id, selected:false})
				return result;
			}, []);
			const sorted_homeroom_teachers = sortArrayOfObjects(homeroom_teachers, 'display', 'text', 'asc');

			filter_options['grades'].push(...sorted_grades);
			filter_options['homerooms'].push(...sorted_homeroom_teachers);

			for(const student of students)
			{
				const student_id = student.student_id;
				const student_first_name = student.first_name;
				const student_last_name = student.last_name;
				const display_name = `${student_last_name}, ${student_first_name}`;
				display_schedule.push({data_id:student_id, display_name:display_name, display_info:student});
			}
		}

		setDisplayData([...display_schedule]);
		setIsLoading(false);
		setDisplayCreated(true);
		setFilterOptions({...filter_options});
	},[currentView, teachers, classrooms, students])

	const toggleOpenConflicts = (data_id) => {
		const data_index = openConflicts.findIndex(display_data_id => display_data_id === data_id);
		if(data_index !== -1)
		{
			openConflicts.splice(data_index, 1);
		}
		else
		{
			openConflicts.push(data_id);
		}
		setOpenConflicts([...openConflicts]);
	}

	const handleToggleOpenDisplayData = (data_id) => {
		const display_data_index = openDisplayData.findIndex(display_data_id => display_data_id === data_id);
		if(display_data_index !== -1)
		{
			openDisplayData.splice(display_data_index, 1);
		}
		else
		{
			openDisplayData.push(data_id);
		}
		setOpenDisplayData([...openDisplayData]);
	}

	const toggleEditData = (dataEditingID) => {
		if(showEditDataModal)
		{
			setShowEditDataModal(false);
			return;
		}

		setDataEditingID(dataEditingID);
		setShowEditDataModal(true);
	}
	
	const changeCurrentView = (new_view) => {
		setIsLoading(true);
		setFilterIDs([]);

		setCurrentView(new_view);
		sessionStorage.setItem('current_view', new_view);
	}

	const createNewBlock = (block_type = null, teacher_id = null, day_number = null, clickTimeRange = null) => {
		toggleEditBlock('new', block_type, teacher_id, day_number, clickTimeRange);
	}

	const toggleEditBlock = (block_id, block_type = null, teacher_id = null, day_number = null, clickTimeRange = null) => {
		let block_data;

		if(!block_id)
		{
			block_data = null;
		}
		else if(block_id === 'new')
		{
			const is_lunch = (block_type === 'lunch') ? '1' : '0';
			const is_pullout = (block_type === 'pullout') ? '1' : '0';
			const is_special = (block_type === 'special') ? '1' : '0';
			const homeroom_teacher_id = (is_pullout !== '1') ? teacher_id : null;
			const actual_teacher_id = (block_type === 'special') ? null : teacher_id;
			const actual_teacher_info = (actual_teacher_id) ?  teachers.find(teacher => teacher.teacher_id === actual_teacher_id) : null;
			const secondary_teachers = [];
			const classroom_id = (actual_teacher_info) ? actual_teacher_info.homeroom_classroom_id : null;
			const student_list = (is_pullout === '0') ? students.reduce((results, student) => {
				if(student.homeroom_teacher_id && student.homeroom_teacher_id === teacher_id) results.push(student.student_id);
				return results;
			}, []) : [];

			block_data = {
				'new': true,
				'block_id': null,
				'block_day_id': null,
				'classroom_id': classroom_id,
				'course_id':null,
				'homeroom_teacher_id': homeroom_teacher_id,
				'is_lunch': is_lunch,
				'is_pullout': is_pullout,
				'is_special': is_special,
				'start_time': clickTimeRange?.start_time || '00:00:00',
				'end_time': clickTimeRange?.end_time || '00:00:00',
				'teacher_id' : actual_teacher_id,
				'day_number' : `${day_number}`,
				'days': [`${day_number}`],
				'block_days': [{block_day_id:null, day_number:`${day_number}`}],
				'student_list' : student_list,
				'locked': '0',
				'secondary_teachers': secondary_teachers
			}
		}
		else
		{
			block_data = blocks.find(block => block.block_id === block_id);
		}

		setBlockData(block_data);
	}

	const toggleLockBlock = async (block_id, locked) => {
		// Update front end
		const block_index = blocks.findIndex(block => block.block_id === block_id);
		if(block_index !== -1) blocks[block_index].locked = locked;
		setBlocks([...blocks]);

		// Update backend
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, block_id:block_id, locked:locked};
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/update-lock-block';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };
	   
		try {
			await API.post(apiName, url, myInit);
		} catch(e)
		{
			console.log(e.response);
		}
	}

	const toggleLockStudentBlock = async (student_id, block_id, locked) => {
		// Update front end
		const student_index = students.findIndex(student => student.student_id === student_id);
		if(student_index !== -1)
		{
			const student_locked_block_index = students[student_index].student_locked_blocks.findIndex(student_locked_block => student_locked_block === block_id);
			if(locked === '0' && student_locked_block_index !== -1) students[student_index].student_locked_blocks.splice(student_locked_block_index, 1);
			if(locked === '1' && student_locked_block_index === -1) students[student_index].student_locked_blocks.push(block_id);
		}
		setStudents([...students]);

		// Update backend
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, student_id:student_id, block_id:block_id, locked:locked};
		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/update-lock-student-block';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };
	   
		try {
			await API.post(apiName, url, myInit);
		} catch(e)
		{
			console.log(e.response);
		}
	}

	const pullNewBlocks = (_callback = ()=>{}) => {
		getData('blocks', '/elementary/get-blocks', {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id}, true, 'elementary').then(returned_data => {

			setBlocks(returned_data);
			setDeletingBlockTargets([]);
			_callback();
		});
	}

	const getBlockDeleteTargetKeys = useCallback((block_id, scope = 'all', dayNumber = null) => {
		const matching_blocks = blocks.filter((block) => `${block.block_id}` === `${block_id}`);
		if(scope === 'day' && dayNumber)
		{
			return matching_blocks
				.filter((block) => `${block.day_number}` === `${dayNumber}`)
				.map((block) => `${block.block_id}-${block.block_day_id || block.day_number}`);
		}

		return matching_blocks.map((block) => `${block.block_id}-${block.block_day_id || block.day_number}`);
	}, [blocks]);

	const saveBlockDaysForBlock = async (target_block, remaining_blocks = []) => {
		const data = {
			school_id: schoolInfo.school_id,
			schedule_version_id: schoolInfo.current_schedule_version_id,
			affected_blocks: remaining_blocks.map((block) => ({
				block_day_id: block.block_day_id || null,
				day_number: `${block.day_number}`
			})),
			block_data: {
				block_id: target_block.block_id,
				course_id: target_block.course_id,
				classroom_id: target_block.classroom_id,
				homeroom_teacher_id: target_block.homeroom_teacher_id,
				teacher_id: target_block.teacher_id,
				start_time: target_block.start_time,
				end_time: target_block.end_time,
				is_lunch: target_block.is_lunch,
				is_pullout: target_block.is_pullout,
				is_special: target_block.is_special
			}
		};

		const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
		const url = '/elementary/save-block';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		await API.post(apiName, url, myInit);
	}

	const removeBlock = async (block_id, e = null, removeOptions = {}) => {
		const { scope = 'all', dayNumber = null, skipConfirm = false } = removeOptions;
		if(e && e.stopPropagation) e.stopPropagation();

		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>{scope === 'day' ? 'Are you sure you want to remove this day from the block?' : 'Are you sure you want to remove this block?'}</p>
				</div>
			)
		}

		if(skipConfirm || await swal(options))
		{
			toggleEditBlock(null);
			const delete_target_keys = getBlockDeleteTargetKeys(block_id, scope, dayNumber);
			setDeletingBlockTargets((current_keys) => {
				const next_keys = [...current_keys];
				delete_target_keys.forEach((delete_key) => {
					if(!next_keys.includes(delete_key)) next_keys.push(delete_key);
				});
				return next_keys;
			});
		
			try {
				if(scope === 'day' && dayNumber)
				{
					const target_block = blocks.find((block) => `${block.block_id}` === `${block_id}`);
					const remaining_blocks = blocks.filter((block) => `${block.block_id}` === `${block_id}` && `${block.day_number}` !== `${dayNumber}`);

					if(target_block && remaining_blocks.length > 0)
					{
						await saveBlockDaysForBlock(target_block, remaining_blocks);
					}
					else
					{
						const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, block_ids:[block_id]};
						const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
						const url = '/elementary/delete-block';
						const myInit = {
							response: true,
							body: JSON.stringify(data),
						};

						await API.post(apiName, url, myInit);
					}
				}
				else
				{
					const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, block_ids:[block_id]};
					const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
					const url = '/elementary/delete-block';
					const myInit = {
						response: true,
						body: JSON.stringify(data),
					};

					await API.post(apiName, url, myInit);
				}

				pullNewBlocks();
			} catch(e)
			{
				console.log(e.response);
				setDeletingBlockTargets((current_keys) => current_keys.filter((current_key) => !delete_target_keys.includes(current_key)));
			}
		}
	}

	const removeStudentBlock = async (student_id, block_id, e = null) => {
		if(e && e.stopPropagation) e.stopPropagation();

		const options =  {
			title: "Are you sure?",
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div>
					<p>Are you sure you want to remove this block from student's schedule?</p>
				</div>
			)
		}

		if(await swal(options))
		{
			/// Update the FRONTEND ///
			const block_index = blocks.findIndex(block => block.block_id === block_id);
			if(block_index !== -1)
			{
				const student_index = blocks[block_index].student_list.findIndex(student => student === student_id);
				if(student_index !== -1) blocks[block_index].student_list.splice(student_index, 1);
			}
			setBlocks([...blocks]);

			/// Update the BACKEND ///
			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, student_id:student_id, block_id:block_id};
			const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
			const url = '/elementary/remove-student-block';
			const myInit = { // OPTIONAL
				response: true,
				body: JSON.stringify(data),
			};
		
			try {
				// Delete block in database
				await API.post(apiName, url, myInit);
			} catch(e)
			{
				console.log(e.response);
			}
		}
	}

	const clearBlocks = async () => {	
		const options =  {
			title: "Are you sure?",
			text: 'This will remove all blocks from the schedule (that are not locked).',
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
					<div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
						<div><strong>Pro Tip:</strong></div>
						<div>If you want to keep certain blocks where they are, make sure to lock that block (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the block) before clearing!</div>
					</div>
				</div>
			)
		}
		
		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				// Update front end
				const new_blocks = blocks.filter(block => block.locked === '1');
				setBlocks(new_blocks);

				// Send to Backend ///
				const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/clear-blocks';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					// Clear blocks
					await API.post(apiName, url, myInit);

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "All non-locked blocks have been cleared!",
					}
					swal(success_options);
				} catch(e)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Clearing Blocks",
						text: "We had an issue clearing your blocks. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(e.response);
				}
			}
		});
	}

	//// Specials Functions ////
	const autoPlaceSpecials = async () => {
		const options =  {
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Start Placing Specials",
					value: true,
					visible: true,
					className: 'blue-btn'
				},
			},
			content: (
				<div>
					<FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
					<h1 className='swal-title' style={{marginTop:'0px'}}>Place Specials</h1>
					<p className='swal-text'>This will try to place all your unplaced specials!</p>
				</div>
				
			)
		}

		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				const placing_in_progress_options =  {
					button:false,
					closeOnClickOutside: false,
					closeOnEsc: false,
					content: (
						<div>
							<FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faCalendarDay}/>
							<h1 className='swal-title' style={{marginTop:'0px'}}>Placing Specials in Progress</h1>
							<p className='swal-text'>Please wait, this may take a few minutes...</p>
							<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
								<div className='red-text'>Do not refresh the page until this process is complete!</div>
							</div>
						</div>
					)
				}

				swal(placing_in_progress_options);

				/// Update the BACKEND ///
				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/auto-place-specials';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					await API.post(apiName, url, myInit);

					// Pull new blocks and then move to next screen
					pullNewBlocks();

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "Your specials should now be placed.",
					}
					swal(success_options);
					
				} catch(error)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Placing Specials",
						text: "We had an issue placing your specials. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(error.response);
				}
			}
		});
	}

	const clearSpecials = async () => {	
		const options =  {
			title: "Are you sure?",
			text: 'This will remove all specials from the schedule (that are not locked).',
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
					<div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
						<div><strong>Pro Tip:</strong></div>
						<div>If you want to keep certain specials where they are, make sure to lock that special (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the special block) before clearing!</div>
					</div>
				</div>
			)
		}
		
		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				// Update front end
				let new_blocks = blocks.filter(block => block.locked === '1' || block.is_special === '0');
				setBlocks(new_blocks);

				// Send to Backend ///
				const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/clear-specials';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					// Clear specials
					await API.post(apiName, url, myInit);

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "All non-locked specials have been cleared!",
					}
					swal(success_options);
				} catch(e)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Clearing Specials",
						text: "We had an issue clearing your specials. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(e.response);
				}
			}
		});
	}

	//// Pullouts Functions ////
	const autoPlacePullouts = async () => {
		const options =  {
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Start Placing Pullouts",
					value: true,
					visible: true,
					className: 'blue-btn'
				},
			},
			content: (
				<div>
					<FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
					<h1 className='swal-title' style={{marginTop:'0px'}}>Place Pullouts</h1>
					<p className='swal-text'>This will try to place all your unplaced pullouts!</p>
				</div>
				
			)
		}

		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				const placing_in_progress_options =  {
					button:false,
					closeOnClickOutside: false,
					closeOnEsc: false,
					content: (
						<div>
							<FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faCalendarDay}/>
							<h1 className='swal-title' style={{marginTop:'0px'}}>Placing Pullouts in Progress</h1>
							<p className='swal-text'>Please wait, this may take a few minutes...</p>
							<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
								<div className='red-text'>Do not refresh the page until this process is complete!</div>
							</div>
						</div>
					)
				}

				swal(placing_in_progress_options);

				/// Update the BACKEND ///
				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/auto-place-pullouts';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					await API.post(apiName, url, myInit);

					// Pull new blocks and then move to next screen
					pullNewBlocks();

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "Your pullouts should now be placed.",
					}
					swal(success_options);
					
				} catch(error)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Placing Pullouts",
						text: "We had an issue placing your pullouts. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(error.response);
				}
			}
		});
	}

	const clearPullouts = async () => {	
		const options =  {
			title: "Are you sure?",
			text: 'This will remove all pullouts from the schedule (that are not locked).',
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
					<div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
						<div><strong>Pro Tip:</strong></div>
						<div>If you want to keep certain pullouts where they are, make sure to lock that pullout (by clicking the <FontAwesomeIcon className='gray-text' icon={faLock}/> icon on the pullout block) before clearing!</div>
					</div>
				</div>
			)
		}
		
		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				// Update front end
				let new_blocks = blocks.filter(block => block.locked === '1' || block.is_pullout === '0');
				setBlocks(new_blocks);

				// Send to Backend ///
				const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/clear-pullouts';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					// Clear specials
					await API.post(apiName, url, myInit);

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "All non-locked pull outs have been cleared!",
					}
					swal(success_options);
				} catch(e)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Clearing Pull Outs",
						text: "We had an issue clearing your pull outs. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(e.response);
				}
			}
		});
	}

	//// Fill Students Functions ////
	const fillStudents = async () => {
		const options =  {
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Start Filling Students",
					value: true,
					visible: true,
					className: 'blue-btn'
				},
			},
			content: (
				<div>
					<FontAwesomeIcon className='modal-top-icon blue-text' icon={faCalendarDay}/>
					<h1 className='swal-title' style={{marginTop:'0px'}}>Fill Students</h1>
					<p className='swal-text'>This will fill all homeroom classes with their homeroom students, as well as try to place any unplaced pullout students into available pullout blocks!</p>
				</div>
				
			)
		}

		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				const placing_in_progress_options =  {
					button:false,
					closeOnClickOutside: false,
					closeOnEsc: false,
					content: (
						<div>
							<FontAwesomeIcon className='modal-top-icon placing-students-in-progress-icon' icon={faCalendarDay}/>
							<h1 className='swal-title' style={{marginTop:'0px'}}>Fill Students in Progress</h1>
							<p className='swal-text'>Please wait, this may take a few minutes...</p>
							<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
								<div className='red-text'>Do not refresh the page until this process is complete!</div>
							</div>
						</div>
					)
				}

				swal(placing_in_progress_options);

				/// Update the BACKEND ///
				const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/fill-students';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					await API.post(apiName, url, myInit);

					// Pull new blocks and then move to next screen
					pullNewBlocks();

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "Your students should now be filled into their blocks.",
					}
					swal(success_options);
					
				} catch(error)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Filling Students",
						text: "We had an issue filling your students. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(error.response);
				}
			}
		});
	}

	const clearStudents = async () => {	
		const options =  {
			title: "Are you sure?",
			text: 'This will remove all students from their schedules (that are not locked).',
			icon: "warning",
			dangerMode: true,
			buttons: {
				cancel: {
					text: "Cancel",
					value: false,
					visible: true,
					className: 'gray-btn'
				},
				confirm: {
					text: "Yes",
					value: true,
					visible: true,
					className: 'red-btn'
				},
			},
			content: (
				<div className='sweet-alert-dont-show-message' style={{padding:'20px 10px 25px 10px'}}>
					<div className='dark-blue-text' style={{textAlign:'left',width:'340px',margin:'auto'}}>
						<div><strong>Pro Tip:</strong></div>
						<div>If you want to keep certain students where they are, make sure to lock that student in those blocks before clearing!</div>
					</div>
				</div>
			)
		}
		
		await swal(options).then(async (return_value) => {
			if(return_value)
			{
				// Update front end
				const new_blocks = blocks.reduce((results, block) => {
					const block_id = block.block_id;
					const block_is_locked = (block.locked === '1');
					const block_student_list = block.student_list;
					const locked_students = block_student_list.filter(student_id => {
						const student_info = students.find(student => student.student_id === student_id);
						const student_locked_blocks = (student_info) ? student_info.student_locked_blocks : [];
						return student_locked_blocks.includes(block_id);
					});

					// if entire block is locked, keep all students
					// otherwise, keep any locked students
					block.student_list = (block_is_locked) ? block_student_list : locked_students;
					results.push(block);
					return results;
				}, []);

				setBlocks(new_blocks);
				setBlockData(null);

				// Send to Backend ///
				const data = {school_id: schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id};
				const apiName = process.env.REACT_APP_ELEM_ENDPOINT_NAME;
				const url = '/elementary/clear-students';
				const myInit = { // OPTIONAL
					response: true,
					body: JSON.stringify(data),
				};
			
				try {
					// Clear blocks
					await API.post(apiName, url, myInit);

					const success_options =  {
						icon:"success",
						title: "Success!",
						text: "All non-locked students have been cleared!",
					}
					swal(success_options);
				} catch(e)
				{
					const failure_options =  {
						icon:"error",
						title: "Error Clearing Students",
						text: "We had an issue clearing your students. Please try again.",
						dangerMode: true,
					}

					swal(failure_options);
					console.log(e.response);
				}
			}
		});
	}

	// const addOpenReport = (report) => {
	// 	const report_index = openReports.findIndex(open_report => open_report === report);
	// 	if(report_index === -1) openReports.push(report);
	// 	setOpenReports([...openReports]);
	// }

	// const removeOpenReport = (report) => {
	// 	const report_index = openReports.findIndex(open_report => open_report === report);
	// 	if(report_index !== -1) openReports.splice(report_index, 1);
	// 	setOpenReports([...openReports]);
	// }

	const showMoreTeachers = () => {
		const num_teachers_to_show = maxTeachersToShow + 10;
		setMaxTeachersToShow(num_teachers_to_show);
	}

	const showMoreStudents = () => {
		const num_students_to_show = maxStudentsToShow + 50;
		setMaxStudentsToShow(num_students_to_show);
	}

	////////////////////////////////
	///// USE EFFECT FUNCTIONS /////
	////////////////////////////////
	// Set background info, as well as create undo/redo
	useEffect(() => {
		const current_view = getJSONFromStorage('current_view', false, 'session');
		if(current_view) setCurrentView(current_view);
	}, []);

	// Set Display Data
	// Create magnetboard display (will also create filter options)
	useEffect(() => {
		if(currentView === 'student')
		{
			if(!scheduleInfoLoading && !blocksLoading && !teachersLoading && !coursesLoading && !classroomsLoading && !studentsLoading && isLoading) createDisplaySchedule();
		}
		else
		{
			if(!scheduleInfoLoading && !blocksLoading && !teachersLoading && !coursesLoading && !classroomsLoading && isLoading) createDisplaySchedule();
		}
	}, [scheduleInfoLoading, blocksLoading, teachersLoading, coursesLoading, classroomsLoading, studentsLoading, currentView, isLoading, createDisplaySchedule]);

	useEffect(() => {
		if(displayDependenciesLoading) return;

		const display_data = (currentView === 'teacher') ? teachers : ((currentView === 'classroom') ? classrooms : ((currentView === 'student') ? students : []));
		if(display_data.length === 0) return;

		const open_data_ids = display_data.map(display_data_info => display_data_info[`${currentView}_id`]);
		setOpenDisplayData(open_data_ids);

		sessionStorage.setItem("current_view", currentView);
		createDisplaySchedule();
	}, [currentView, teachers, classrooms, students, createDisplaySchedule, displayDependenciesLoading]);

	useEffect(() => {
		const num_days_in_cycle = scheduleInfo.num_days_in_cycle;
		let new_days = [];

		for(let day = 1; day <= num_days_in_cycle; day++)
		{
			new_days.push(day);
		}

		setDays(new_days);
	}, [scheduleInfo]);
	
	// Do search
	useEffect(() => {
		if(!searchValue) 
		{
			setFilterIDs([]);
			return;
		}

		if(currentView === 'teacher')
		{
			const teacher_results = teacherSearchFuse.search(searchValue);
			setFilterIDs(teacher_results.slice(0, 6).map((result) => result.item.teacher_id));
		}
		else if(currentView === 'classroom')
		{
			const classroom_results = classroomSearchFuse.search(searchValue);
			setFilterIDs(classroom_results.slice(0, 6).map((result) => result.item.classroom_id));
		}
		else if(currentView === 'student')
		{
			const student_results = studentSearchFuse.search(searchValue);
			setFilterIDs(student_results.slice(0, 30).map((result) => result.item.student_id));
		}
	}, [searchValue, teacherSearchFuse, classroomSearchFuse, studentSearchFuse, currentView]);
	
	// Update filtered data
	useEffect(() => {
		let filtered_data = displayData.reduce((result, display_data) => {
			let include = 0;
			let include_specific_data = 0;
			let include_for_teacher_type = 0;
			let include_for_grade = 0;
			let include_for_homeroom = 0;

			Object.keys(filterOptions).forEach(filter_title => {
				const filter_options = filterOptions[filter_title];

				// If none of the options are selected, then automatically include all of them
				if(filter_options.filter(option => option.selected).length === 0)
				{
					if(filter_title === 'teachers')
					{
						include_specific_data = 1;
					}
					else if(filter_title === 'type')
					{
						include_for_teacher_type = 1;
					}
					else if(filter_title === 'classrooms')
					{
						include_specific_data = 1;
					}
					else if(filter_title === 'grades')
					{
						include_for_grade = 1;
					}
					else if(filter_title === 'homerooms')
					{
						include_for_homeroom = 1;
					}
				}
				
				// Loop through all filter options and start filtering those that apply
				filter_options.forEach(filter_option => {
					const filter_option_selected = filter_option.selected;
					if(!filter_option_selected) return;
	
					const filter_option_value = filter_option.value;
	
					if(currentView === 'teacher')
					{
						if(filter_title === 'teachers' && display_data.display_info.teacher_id === filter_option_value) include_specific_data = 1;
						
						if(filter_title === 'type')
						{
							const is_homeroom_teacher = display_data.display_info.is_homeroom_teacher;
							const is_specials_teacher = display_data.display_info.is_specials_teacher;
							const is_pullout_teacher = display_data.display_info.is_pullout_teacher;
							
							const teacher_teaches = ('teacher_can_teach' in display_data.display_info) ? display_data.display_info.teacher_can_teach.reduce((result, tct_course_info) => {
								const tct_course_id = tct_course_info.course_id;
								const course_info = courseById.get(`${tct_course_id}`);
								if(course_info && course_info.is_special === '1') result.teacher_teaches_specials = true;
								if(course_info && course_info.is_pullout === '1') result.teacher_teaches_pullouts = true;
								return result;
							}, {teacher_teaches_specials: false, teacher_teaches_pullouts: false}) : {teacher_teaches_specials: false, teacher_teaches_pullouts: false};
							const teacher_teaches_specials = teacher_teaches.teacher_teaches_specials;
							const teacher_teaches_pullouts = teacher_teaches.teacher_teaches_pullouts;

							if(filter_option_value === 'is_homeroom' && is_homeroom_teacher === '1')
							{
								include_for_teacher_type = 1;
							}
							else if(filter_option_value === 'is_specials' && (teacher_teaches_specials || is_specials_teacher === '1'))
							{
								include_for_teacher_type = 1;
							}
							else if(filter_option_value === 'is_pullout' && (teacher_teaches_pullouts || is_pullout_teacher === '1'))
							{
								include_for_teacher_type = 1;
							}
						}
					}
					else if(currentView === 'classroom')
					{
						if(filter_title === 'classrooms' && display_data.display_info.classroom_id === filter_option_value) include_specific_data = 1;
					}
					else if(currentView === 'student')
					{
						if(filter_title === 'grades' && display_data.display_info.grade === filter_option_value) include_for_grade = 1;
						if(filter_title === 'homerooms' && display_data.display_info.homeroom_teacher_id === filter_option_value) include_for_homeroom = 1;
					}
				});
			});

			if(currentView === 'teacher' && include_for_teacher_type === 1 && include_specific_data === 1) include = 1;
			if(currentView === 'classroom' && include_specific_data === 1) include = 1;
			if(currentView === 'student' && include_for_grade === 1 && include_for_homeroom === 1) include = 1;

			if(include === 1) result.push(display_data.display_info[`${currentView}_id`]);

			return result;
		}, []);
		
		setFilteredData([...filtered_data]);
	}, [filterOptions, courseById, currentView, displayData]);

	/////////////////////////////
	//// CALCULATE CONFLICTS ////
	/////////////////////////////
	// useEffect(() => {
	// 	if(teachersLoading || studentsLoading || coursesLoading || classroomsLoading || blocksLoading || periodsLoading) return;

	// 	let new_conflicts = [];
	// 	let checked_blocks = [];
	// 	for(const block of blocks)
	// 	{
	// 		const block_id = block.block_id;
	// 		const block_teacher_id = block.teacher_id;
	// 		const block_homeroom_teacher_id = block.homeroom_teacher_id;
	// 		const block_course_id = block.course_id;
	// 		const block_classroom_id = block.classroom_id;
	// 		const block_day_number = block.day_number;
	// 		const block_start_time = block.start_time;
	// 		const block_end_time = block.end_time;
	// 		const block_time = {start_time:block_start_time, end_time:block_end_time};
	// 		const block_student_list = (block_homeroom_teacher_id) ? students.reduce((results, student) => {
	// 			if(student.homeroom_teacher_id && student.homeroom_teacher_id === block_homeroom_teacher_id) results.push(student.student_id);
	// 			return results;
	// 		}, []) : (('student_list' in block) ? block.student_list : []);
	// 		const block_is_pullout = block.is_pullout;
	// 		const block_is_special = block.is_special;
	// 		const block_is_lunch = block.is_lunch;

	// 		const block_start_time_formatted = formatTime(block_start_time);
	// 		const block_end_time_formatted = formatTime(block_end_time);

	// 		const block_teacher_info = (block_teacher_id) ? teachers.find(teacher => teacher.teacher_id === block_teacher_id) : null;
	// 		const block_homeroom_teacher_info = (block_homeroom_teacher_id) ? teachers.find(teacher => teacher.teacher_id === block_homeroom_teacher_id) : null;
	// 		const block_course_info = courses.find(course => course.course_id === block_course_id);
	// 		const block_classroom_info = (block_classroom_id) ? classrooms.find(classroom => classroom.classroom_id === block_classroom_id) : null;

	// 		const block_teacher_display_name = (block_teacher_info) ? `${block_teacher_info.name}, ${block_teacher_info.first_name}` : null;
	// 		const block_homeroom_teacher_display_name = (block_homeroom_teacher_info) ? `${block_homeroom_teacher_info.name}, ${block_homeroom_teacher_info.first_name}` : null;

	// 		const block_classroom_name = (block_classroom_info) ? block_classroom_info.classroom_name : null;
			
	// 		const block_course_name = (block_course_info) ? block_course_info.name : null;
	// 		const block_course_day_period_restrictions = (block_course_info) ? block_course_info.day_period_restrictions : [];

	// 		// Calculate course outside usual classroom conflicts
	// 		// Calculate course missing a classroom conflicts
	// 		const course_classroom_restrictions = (block_course_info) ? block_course_info.classroom_restrictions : [];
	// 		const course_outside_normal_classroom = (course_classroom_restrictions && course_classroom_restrictions.length > 0 && !course_classroom_restrictions.includes(block_classroom_id));
	// 		const course_missing_classroom = (course_classroom_restrictions && course_classroom_restrictions.length > 0 && !block_classroom_id);
			
	// 		if(course_outside_normal_classroom) new_conflicts.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_course_name} is not in its usual classroom(s) on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});
	// 		if(course_missing_classroom) new_conflicts.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_course_name} is not in a classroom(s) on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});

	// 		// Calculate teacher day period restrictions conflicts
	// 		const teacher_period_restrictions_conflicts = (block_teacher_info) ? block_teacher_info.day_period_restrictions.reduce((results, day_period_restriction) => {
	// 			const day_number_restriction = day_period_restriction.day_number;

	// 			const period_id_restriction = day_period_restriction.period_id;
	// 			const period_info = periods.find(period => period.period_id === period_id_restriction);
	// 			const period_start_time = period_info.start_time;
	// 			const period_end_time = period_info.end_time;
	// 			const period_time = {start_time:period_start_time, end_time:period_end_time};

	// 			if(block_day_number === day_number_restriction && overlapCheck(block_time, period_time)) results.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_teacher_info.name}, ${block_teacher_info.first_name} is in a restricted time Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});
				
	// 			return results;
	// 		}, []) : [];

	// 		// Calculate all double booked conflicts (teachers, classrooms, and students)
	// 		const double_booked_conflicts = blocks.reduce((double_booked_tracker, double_booked_block) => {
	// 			const double_booked_block_id = double_booked_block.block_id;
	// 			const double_booked_block_is_pullout = double_booked_block.is_pullout;
	// 			const double_booked_block_is_special = double_booked_block.is_special;
	// 			const double_booked_block_is_lunch = double_booked_block.is_lunch;

	// 			// Skip same blocks or blocks that have already been checked
	// 			if(checked_blocks.includes(double_booked_block_id)) return double_booked_tracker;
	// 			if(block_id === double_booked_block_id) return double_booked_tracker;

	// 			const double_booked_block_day_number = double_booked_block.day_number;
	// 			const double_booked_block_start_time = double_booked_block.start_time;
	// 			const double_booked_block_end_time = double_booked_block.end_time;
	// 			const double_booked_block_time = {start_time:double_booked_block_start_time, end_time:double_booked_block_end_time};

	// 			const day_and_time_overlap = (block_day_number === double_booked_block_day_number && overlapCheck(block_time, double_booked_block_time));

	// 			if(day_and_time_overlap)
	// 			{
	// 				const double_booked_teacher_id = double_booked_block.teacher_id;
	// 				const double_booked_homeroom_teacher_id = double_booked_block.homeroom_teacher_id;
	// 				const double_booked_classroom_id = double_booked_block.classroom_id;
	// 				const double_booked_student_list = ('student_list' in double_booked_block) ? double_booked_block.student_list : [];
	// 				const double_booked_teacher_and_homeroom_teacher_are_same = (double_booked_teacher_id === double_booked_homeroom_teacher_id);

	// 				const double_booked_students = (double_booked_homeroom_teacher_id) ? students.reduce((double_booked_student_results, student) => {
	// 					const student_id = student.student_id;
	// 					const student_homeroom_teacher_id = student.homeroom_teacher_id;
	// 					if(student_homeroom_teacher_id === double_booked_homeroom_teacher_id && !double_booked_student_results.includes(student_id)) double_booked_student_results.push(student_id);
	// 					return double_booked_student_results;
	// 				}, [...double_booked_student_list]) : double_booked_student_list;

	// 				const student_intersection = block_student_list.filter(value => double_booked_students.includes(value));

	// 				const block_teacher_match =  (block_teacher_id && (block_teacher_id === double_booked_teacher_id || block_teacher_id === double_booked_homeroom_teacher_id));
	// 				const block_homeroom_teacher_match = (block_homeroom_teacher_id && (block_homeroom_teacher_id === double_booked_teacher_id || block_homeroom_teacher_id === double_booked_homeroom_teacher_id));
	// 				const block_classroom_match = (block_classroom_id && (block_classroom_id === double_booked_classroom_id));
	// 				const block_student_match = (!(block_is_pullout === '1' && double_booked_block_is_special !== '1' && double_booked_block_is_pullout !== '1' && double_booked_block_is_lunch !== '1') && !(double_booked_block_is_pullout === '1' && block_is_special !== '1' && block_is_pullout !== '1' && block_is_lunch !== '1'));

	// 				if(block_teacher_match) double_booked_tracker.teachers_double_booked.push({block_id:block_id, teachers:[block_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_teacher_display_name} is double booked on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});

	// 				if(block_homeroom_teacher_match && !double_booked_teacher_and_homeroom_teacher_are_same) double_booked_tracker.teachers_double_booked.push({block_id:block_id, teachers:[block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_homeroom_teacher_display_name} is double booked on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});

	// 				if(block_classroom_match) double_booked_tracker.classrooms_double_booked.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_classroom_name} is double booked on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});

	// 				if(block_student_match && student_intersection.length > 0)
	// 				{
	// 					const student_names = student_intersection.reduce((student_names_array, student_id) => {
	// 						const student_info = students.find(student => student.student_id === student_id);
	// 						const student_first_name = student_info.first_name;
	// 						const student_last_name = student_info.last_name;
	// 						const student_name = `${student_last_name}, ${student_first_name}`;
	// 						student_names_array.push(student_name);
	// 						return student_names_array;
	// 					},[]);
	// 					const comma_separated_student_names = student_names.join(", ");

	// 					double_booked_tracker.students_double_booked.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:student_intersection, display:`${comma_separated_student_names} is/are double booked on Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});
	// 				}
	// 			}
				
	// 			return double_booked_tracker;
	// 		}, {teachers_double_booked:[], classrooms_double_booked:[], students_double_booked:[]});

	// 		// Calculate course day period restrictions conflicts
	// 		const course_period_restrictions_conflicts = block_course_day_period_restrictions.reduce((results, day_period_restriction) => {
	// 			const day_number_restriction = day_period_restriction.day_number;

	// 			const period_id_restriction = day_period_restriction.period_id;
	// 			const period_info = periods.find(period => period.period_id === period_id_restriction);
	// 			const period_start_time = (period_info) ? period_info.start_time : null;
	// 			const period_end_time = (period_info) ? period_info.end_time : null;

	// 			if(!period_info || !period_start_time || !period_end_time) return results;
				
	// 			const period_time = {start_time:period_start_time, end_time:period_end_time};

	// 			if(block_day_number === day_number_restriction && overlapCheck(block_time, period_time)) results.push({block_id:block_id, teachers:[block_teacher_id, block_homeroom_teacher_id], course_id:block_course_id, classroom_id:block_classroom_id, students:block_student_list, display:`${block_course_name} is in a restricted time Day ${block_day_number} from ${block_start_time_formatted}-${block_end_time_formatted}`});
				
	// 			return results;
	// 		}, []);

	// 		new_conflicts = [...new_conflicts, ...teacher_period_restrictions_conflicts, ...double_booked_conflicts.teachers_double_booked, ...double_booked_conflicts.classrooms_double_booked, ...double_booked_conflicts.students_double_booked, ...course_period_restrictions_conflicts];

	// 		checked_blocks.push(block_id);
	// 	}

	// 	const homeroom_teacher_missing_special_conflicts = courses.reduce((results, course) => {
	// 		const course_id = course.course_id;
	// 		const course_name = course.name;
	// 		const course_homerooms = course.course_homerooms;
	// 		const course_num_times_needed = course.num_times_needed;

	// 		for(const homeroom_teacher_id of course_homerooms)
	// 		{
	// 			const homeroom_teacher_info = teachers.find(teacher => teacher.teacher_id === homeroom_teacher_id);
	// 			const num_times_homeroom_has_course = blocks.reduce((results, temp_block) => {
	// 				if(temp_block.homeroom_teacher_id === homeroom_teacher_id && temp_block.course_id === course_id) results++;
	// 				return results;
	// 			}, 0);

	// 			if(num_times_homeroom_has_course < course_num_times_needed) results.push({block_id:null, teachers:[homeroom_teacher_id], course_id:course_id, classroom_id:null, students:[], display:`${homeroom_teacher_info.name}, ${homeroom_teacher_info.first_name} is missing ${course_num_times_needed - num_times_homeroom_has_course} period of ${course_name}`});
	// 		}

	// 		return results;
	// 	}, []);

	// 	const students_missing_pullout_requirement = courses.reduce((results, course) => {
	// 		const course_id = course.course_id;
	// 		const course_name = course.name;
	// 		const pullout_groups = course.pullout_groups;

	// 		for(const pullout_group_id in pullout_groups)
	// 		{
	// 			const pullout_group = pullout_groups[pullout_group_id];
	// 			const course_num_times_needed = pullout_group.num_times_needed;
	// 			const course_duration_needed = pullout_group.duration;

	// 			for(const pullout_student_id of pullout_group.student_list)
	// 			{
	// 				const pullout_student_info = students.find(student => student.student_id === pullout_student_id);
	// 				const num_times_student_has_course = blocks.reduce((results, temp_block) => {
	// 					if(temp_block.student_list.includes(pullout_student_id) && temp_block.course_id === course_id) results++;
	// 					return results;
	// 				}, 0);

	// 				if(num_times_student_has_course < course_num_times_needed) results.push({block_id:null, teachers:[], course_id:course_id, classroom_id:null, students:[pullout_student_id], display:`${pullout_student_info.last_name}, ${pullout_student_info.first_name} is missing ${course_num_times_needed - num_times_student_has_course} period(s) of ${course_name}`});
	// 			}
	// 		}

	// 		return results;
	// 	}, []);

	// 	new_conflicts = [...new_conflicts, ...homeroom_teacher_missing_special_conflicts, ...students_missing_pullout_requirement];
	// 	setConflicts(new_conflicts);
	// }, [teachersLoading, studentsLoading, coursesLoading, classroomsLoading, blocksLoading, teachers, students, courses, classrooms, blocks]);

	////////////////////////////////
	///// INITIALIZE VARIABLES /////
	////////////////////////////////
	let teacher_counter = 1;
	let student_counter = 1;

	let selected_filters = [];
	Object.keys(filterOptions).forEach(filter_title => {
		const filter_options = filterOptions[filter_title];

		filter_options.forEach(filter_option => {
			if(filter_option.selected) selected_filters.push(filter_option);
		});
	});

	const blockMatchesCurrentSchedule = (block, dataId, backgroundInfo = {}) => {
		if(currentView === 'teacher')
		{
			const secondaryTeachers = ('secondary_teachers' in block) ? block.secondary_teachers : [];
			const studentList = ('student_list' in block) ? block.student_list : [];
			const homeroomStudents = backgroundInfo.teacher_homeroom_students || [];

			return (
				block.teacher_id === dataId ||
				block.homeroom_teacher_id === dataId ||
				secondaryTeachers.includes(dataId) ||
				(block.is_pullout === '1' && studentList.some((studentId) => homeroomStudents.includes(studentId)))
			);
		}

		if(currentView === 'classroom') return block.classroom_id === dataId;

		if(currentView === 'student')
		{
			const studentList = ('student_list' in block) ? block.student_list : [];
			return studentList.includes(dataId);
		}

		return false;
	};

	const getDisplayDaySchedule = (dataId, dayNumber, backgroundInfo = {}) => buildDaySchedule({
		items: blocks,
		dayNumber,
		lengthKey: 'block_length',
		filterItem: (block, tempDayNumber) => {
			if(block.new) return false;
			if(`${block.day_number}` !== `${tempDayNumber}`) return false;
			return blockMatchesCurrentSchedule(block, dataId, backgroundInfo);
		}
	});

	const getDisplayDayFloatingBlocks = (dataId, dayNumber, backgroundInfo = {}) => buildDaySchedule({
		items: blocks,
		dayNumber,
		lengthKey: 'block_length',
		filterItem: (block, tempDayNumber) => {
			if(!block.new) return false;
			if(`${block.day_number}` !== `${tempDayNumber}`) return false;
			return blockMatchesCurrentSchedule(block, dataId, backgroundInfo);
		}
	});

	const getDisplayDayConflictKeys = (dataId, dayNumber, backgroundInfo = {}) => {
		const dayBlocks = buildDaySchedule({
			items: blocks,
			dayNumber,
			lengthKey: 'block_length',
			filterItem: (block, tempDayNumber) => {
				if(`${block.day_number}` !== `${tempDayNumber}`) return false;
				return blockMatchesCurrentSchedule(block, dataId, backgroundInfo);
			}
		});

		return getTimeGridConflictKeys(dayBlocks, (block) => `${block.block_day_id || block.block_id}-${dayNumber}`);
	};

	const formatScheduleBlockName = (block, dataId) => {
		const courseInfo = courses.find((course) => course.course_id === block.course_id);
		const courseCode = courseInfo ? courseInfo.course_code : null;
		const baseBlockName = (block.course_id && courseInfo) ? `${courseInfo.name} ${courseCode && `(${courseCode})`}` : ((block.is_lunch === '1') ? 'Lunch' : 'Untitled');

		const teacherInfo = block.teacher_id ? teachers.find((teacher) => teacher.teacher_id === block.teacher_id) : null;
		const teacherLastName = teacherInfo ? teacherInfo.name : '';

		const homeroomTeacherInfo = block.homeroom_teacher_id ? teachers.find((teacher) => teacher.teacher_id === block.homeroom_teacher_id) : null;
		const homeroomTeacherLastName = homeroomTeacherInfo ? homeroomTeacherInfo.name : '';

		if(currentView === 'teacher')
		{
			const currentTeacherIsTeacher = block.teacher_id === dataId;
			if(block.is_special === '1')
			{
				if(currentTeacherIsTeacher) return `${courseCode} ${homeroomTeacherLastName && `- ${homeroomTeacherLastName}`}`;
				return `${baseBlockName} ${teacherLastName && `- ${teacherLastName}`}`;
			}

			if(currentTeacherIsTeacher) return baseBlockName;
			return `${baseBlockName} ${teacherLastName && `- ${teacherLastName}`}`;
		}

		if(currentView === 'classroom')
		{
			if(block.teacher_id !== block.homeroom_teacher_id) return `${baseBlockName} ${teacherLastName && `- ${teacherLastName}`} ${homeroomTeacherLastName && `- ${homeroomTeacherLastName}`}`;
			return `${baseBlockName} ${teacherLastName && `- ${teacherLastName}`}`;
		}

		if(currentView === 'student') return `${baseBlockName} ${teacherLastName && `- ${teacherLastName}`}`;

		return baseBlockName;
	};

	const getScheduleBlockColor = (block) => {
		if(block.is_lunch === '1') return 'pastel-yellow';
		if(block.is_special === '1') return 'green';
		if(block.is_pullout === '1') return 'orange';
		return 'dark-purple';
	};

	const getDisplayDayOverlays = (_dataId, dayNumber) => buildDayOverlays({
		overlays: [],
		dayNumber
	});
	
		//console.log({blocks, courses, teachers, classrooms, students, periods});
	//console.log({displayData});
	//console.log({isLoading, displayCreated});
	//console.log({filterIDs, filteredData});
	//console.log({conflicts});

	return (
		<>
		{showEditDataModal &&
			<>
			{currentView === 'teacher' ? 
			(
				<EditTeacherData schoolInfo={schoolInfo} dataID={dataEditingID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} periods={periods} teachers={teachers} courses={courses} classrooms={classrooms} students={students} setStudents={setStudents} setDatabaseData={setTeachers} filteredResults={teachers} setFilteredResults={()=>{}}/>
			):(currentView === 'classroom') ?
			(
				<EditClassroomData schoolInfo={schoolInfo} dataID={dataEditingID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} classrooms={classrooms} courses={courses} teachers={teachers} setDatabaseData={setClassrooms} filteredResults={classrooms} setFilteredResults={()=>{}} blocks={blocks}/>
			):(currentView === 'student') ?
			(
				<EditStudentData schoolInfo={schoolInfo} dataID={dataEditingID} toggleEditData={toggleEditData} scheduleInfo={scheduleInfo} students={students} courses={courses} teachers={teachers} setDatabaseData={setStudents} blocks={blocks}/>
			):null}
			</>
		}
		<EditBlock schoolInfo={schoolInfo} days={days} blockData={blockData} blocks={blocks} setBlocks={setBlocks} courses={courses} setCourses={setCourses} teachers={teachers} classrooms={classrooms} students={students} overlapCheck={timesOverlap} removeBlock={removeBlock} toggleEditBlock={toggleEditBlock} sidePanelClosing={sidePanelClosing} setSidePanelClosing={setSidePanelClosing}/>
		<DndProvider backend={HTML5Backend}>
			<div className='mb-elem-container'>
				<div className='mb-elem-top-bar'>
					<div className='mb-top-bar-inner'>
						<div className='mb-elem-schedule-name-container'>
							<h1><span className='capitalize'>{currentView}</span> Schedules</h1>
						</div>
						<div className='mb-menu-bar' ref={menu_ref}>
							<div className='mb-menu-btn' onClick={() => toggleMenuItem('file')}>
								<span>File</span>
								{currentOpenMenu === 'file' &&
									<div className='mb-menu-dropdown-options-container'>
										<div className='mb-dropdown-option' onClick={openPrint}>Print Schedule</div>
										{/* <div className={`mb-dropdown-option ${!schoolSubscribed && 'gray-text'}`} onClick={schoolSubscribed ? ()=>setShowExportScheduleModal(true) : () => alert("Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!")}>Export Schedule</div> */}
									</div>
								}
							</div>
							<div className='mb-menu-btn' onClick={() => toggleMenuItem('actions')}>
								<span>Actions</span>
								{currentOpenMenu === 'actions' &&
									<div className='mb-menu-dropdown-options-container'>
										<div className='mb-dropdown-option mb-dropdown-option-hover' data-actiontype='blocks' onMouseEnter={() => handleDropdownHoverShow('blocks')} onMouseLeave={() => handleDropdownHoverHide('blocks')}>
											<div>Blocks</div>
											<FontAwesomeIcon icon={faChevronRight} />
										</div>
										<div className='mb-dropdown-option mb-dropdown-option-hover' data-actiontype='specials'  onMouseEnter={() => handleDropdownHoverShow('specials')} onMouseLeave={() => handleDropdownHoverHide('specials')}>
											<div>Specials</div>
											<FontAwesomeIcon icon={faChevronRight}/>
										</div>
										<div className='mb-dropdown-option mb-dropdown-option-hover' data-actiontype='pullouts'  onMouseEnter={() => handleDropdownHoverShow('pullouts')} onMouseLeave={() => handleDropdownHoverHide('pullouts')}>
											<div>Pullouts</div>
											<FontAwesomeIcon icon={faChevronRight}/>
										</div>
										<div className='mb-dropdown-option mb-dropdown-option-hover' data-actiontype='students'  onMouseEnter={() => handleDropdownHoverShow('students')} onMouseLeave={() => handleDropdownHoverHide('students')}>
											<div>Students</div>
											<FontAwesomeIcon icon={faChevronRight}/>
										</div>
									</div>
								}
								<div className='mb-dropdown-options-subcontainer hide' data-actiontype='blocks' onMouseEnter={() => handleDropdownHoverShow('blocks')} onMouseLeave={() => handleDropdownHoverHide('blocks')}>
									<div className='mb-dropdown-option' onClick={clearBlocks}>Clear All Blocks</div>
								</div>
								<div className='mb-dropdown-options-subcontainer hide' data-actiontype='specials' onMouseEnter={() => handleDropdownHoverShow('specials')} onMouseLeave={() => handleDropdownHoverHide('specials')}>
									<div className='mb-dropdown-option' onClick={clearSpecials}>Clear Specials</div>
									<div className={`mb-dropdown-option ${!schoolSubscribed && 'gray-text'}`} onClick={schoolSubscribed ? autoPlaceSpecials : () => alert("Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!")}>Auto-Place Specials</div>
								</div>
								<div className='mb-dropdown-options-subcontainer hide' data-actiontype='pullouts' onMouseEnter={() => handleDropdownHoverShow('pullouts')} onMouseLeave={() => handleDropdownHoverHide('pullouts')}>
									<div className='mb-dropdown-option' onClick={clearPullouts}>Clear Pullouts</div>
									<div className={`mb-dropdown-option ${!schoolSubscribed && 'gray-text'}`} onClick={schoolSubscribed ? autoPlacePullouts : () => alert("Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!")}>Auto-Place Pullouts</div>
								</div>
								<div className='mb-dropdown-options-subcontainer hide' data-actiontype='students' onMouseEnter={() => handleDropdownHoverShow('students')} onMouseLeave={() => handleDropdownHoverHide('students')}>
									<div className='mb-dropdown-option' onClick={clearStudents}>Clear Students</div>
									<div className={`mb-dropdown-option ${!schoolSubscribed && 'gray-text'}`} onClick={schoolSubscribed ? fillStudents : () => alert("Sorry, this feature is only available with a paid subscription. Please contact us at contact@edario.com to get full access!")}>Fill Students</div>
								</div>
							</div>
							<div className='mb-menu-btn'>
								<span>Help</span>
							</div>
						</div>
						<div className='mb-options-bar'>
							<div className='mb-search-bar-outer-container'>
								<div className='mb-search-bar-container'>
									<MagnetboardSearchInput
										value={searchValue}
										onSearchChange={setSearchValue}
										placeholder={`Search for a teacher...`}
									/>
									<FontAwesomeIcon className='mb-search-bar-icon' icon={faSearch}/>
									<div className='mb-search-bar-filter-container' ref={filter_ref}>
										<div style={{display:'grid',width:'100%', height:'100%', alignItems:'center', justifyItems:'center'}} onClick={magnetboardOptions.allow_filtering ? toggleFilterOptions : ()=>{}}>
											<FontAwesomeIcon icon={faSlidersH}/>
										</div>
										{filterDataVisible &&
											<div className='list-filter-options-container click-restricted' style={{top:'60px'}}>
												<div className='small-text'>Filter by:</div>
												{Object.keys(filterOptions).map((filter_title, j) => {
													const filter_options = filterOptions[filter_title];
													const filter_options_open = openFilterOptions.includes(filter_title);
													const filter_group_selected_count = filter_options.filter((filter_option) => filter_option.selected).length;

													return (
														<div key={j}>
															<div className='list-filter-title-container' onClick={() => updateOpenFilters(filter_title, filter_options_open)}>
																<div className='capitalize list-filter-title-label'>
																	{filter_title}
																	{filter_group_selected_count > 0 &&
																		<span className='list-filter-title-selected-indicator'>{filter_group_selected_count}</span>
																	}
																</div>
																<FontAwesomeIcon className='small-text' icon={filter_options_open ? faChevronUp : faChevronDown}/>
															</div>
															{filter_options_open && filter_options.map((filter_option, i) => {
																const filter_display = filter_option.display;
																const filter_selected = filter_option.selected;

																return (
																	<div className='parent-div list-filter-option click-restricted' key={i} onClick={() => updateFilterOptions(filter_title, i, !filter_selected)}>
																		<div className='list-filter-option-name click-restricted'>{filter_display}</div>
																		{filter_selected &&
																			<FontAwesomeIcon className='fas-checkbox-checked click-restricted' icon={faCheckSquare}/>
																		}
																		{!filter_selected &&
																			<FontAwesomeIcon className='fas-checkbox-unchecked click-restricted' icon={faSquare}/>
																		}
																	</div>
																)
															})}
														</div>
													)
												})}
											</div>
										}
									</div>
								</div>
							</div>
							<div className='mb-change-view-container'>
								{magnetboardOptions.view_options &&
									<div className='mb-dropdown-wrapper'><Dropdown data={magnetboardOptions.view_options} currentValue={currentView} handleChange={(new_view) => changeCurrentView(new_view)} /></div>
								}
							</div>
						</div>
						<div className ='mb-selected-filters-bar'>
							{selected_filters.length > 0 &&
								<>
								<div className='mb-selected-filters-text'>Filters:</div>
								{Object.keys(filterOptions).map((filter_title, i) => {
									const filter_options = filterOptions[filter_title];

									return (
										<React.Fragment key={i}>
										{filter_options.map((filter_option, i) => {
											const filter_option_selected = filter_option.selected;
											
											if(!filter_option_selected) return null;

											return (
												<div className='mb-selected-filters-filter-tag' key={i}>
													<div className='mb-selected-filters-filter-tag-inner-container'>
														<div>{filter_option.display}</div>
														<FontAwesomeIcon className='dark-gray-link' icon={faTimes} onClick={() => updateFilterOptions(filter_title, i, false)} />
													</div>
												</div>
											)
										})}
										</React.Fragment>
									)
								})}
								</>
							}
						</div>
					</div>
				</div>
				<div ref={printRef}>
				{(isLoading || !displayCreated) ?
					(
						<div className='mb-message-container'>
							<img src={require('../../images/balls.gif')} alt='loading...' style={{height:'80px'}}/>
						</div>
					):(
						<>
						{displayData.map((display_data, display_index) => {
							const data_id = display_data.data_id;
							const display_name = display_data.display_name;
							const display_info = display_data.display_info;

							if((filterIdSet.size > 0 && !filterIdSet.has(`${data_id}`)) || !filteredDataSet.has(`${data_id}`)) return null;
							if(currentView === 'teacher' && magnetboardOptions.allowed_teacher_ids && !magnetboardOptions.allowed_teacher_ids.includes(data_id)) return null;
							if(currentView === 'student' && student_counter > maxStudentsToShow) return null;						
							if(currentView === 'student') student_counter++;

							let background_info = {};
							const data_conflicts = (currentView === 'teacher')
								? (conflictsByView.teacherConflicts.get(`${data_id}`) || [])
								: ((currentView === 'classroom')
									? (conflictsByView.classroomConflicts.get(`${data_id}`) || [])
									: (conflictsByView.studentConflicts.get(`${data_id}`) || []));

							if(currentView === 'teacher')
							{
								const teacher_classroom_id = display_info.homeroom_classroom_id;
								const teacher_classroom_info = teacher_classroom_id ? classroomById.get(`${teacher_classroom_id}`) : null;
								const teacher_classroom = (teacher_classroom_info) ? teacher_classroom_info.classroom_name : null;
								const teacher_homeroom_students = homeroomStudentsByTeacherId.get(`${data_id}`) || [];
								const number_homeroom_students = teacher_homeroom_students.length;

								background_info = {teacher_classroom:teacher_classroom, teacher_homeroom_students:teacher_homeroom_students, number_homeroom_students:number_homeroom_students};
							}
							else if(currentView === 'classroom')
							{
								const homeroom_teacher_id = display_info.homeroom_teacher_id;
								const homeroom_teacher_info = homeroom_teacher_id ? teacherById.get(`${homeroom_teacher_id}`) : null;
								const homeroom_teacher_first_name = (homeroom_teacher_info) ? homeroom_teacher_info.first_name : null;
								const homeroom_teacher_last_name = (homeroom_teacher_info) ? homeroom_teacher_info.name : null;
								const homeroom_teacher_name = (homeroom_teacher_id) ? `${homeroom_teacher_last_name}, ${homeroom_teacher_first_name}` : null;

								background_info = {homeroom_teacher_name:homeroom_teacher_name};
							}
							else if(currentView === 'student')
							{
								const student_grade = display_info.grade;

								const homeroom_teacher_id = display_info.homeroom_teacher_id;
								const homeroom_teacher_info = homeroom_teacher_id ? teacherById.get(`${homeroom_teacher_id}`) : null;
								const homeroom_teacher_first_name = (homeroom_teacher_info) ? homeroom_teacher_info.first_name : null;
								const homeroom_teacher_last_name = (homeroom_teacher_info) ? homeroom_teacher_info.name : null;
								const homeroom_teacher_name = (homeroom_teacher_id) ? `${homeroom_teacher_last_name}, ${homeroom_teacher_first_name}` : null;

								background_info = {grade:student_grade, homeroom_teacher_name:homeroom_teacher_name};
							}

							const data_open = openDisplayData.includes(data_id);

							return (
								<div className='mb-elem-teacher-schedule' key={display_index}>
									<div className='mb-elem-teacher-row'>
										<div className='mb-elem-teacher-info'>
											<h1 className='mb-elem-teacher-name'>{display_name}</h1>
											{currentView === 'teacher' ? 
											(
											<>
											{(display_info.is_homeroom_teacher === '1') &&
												<div className='mb-elem-homeroom-info mb-hide-during-print'>
													<div className='mb-elem-homeroom-info-line'><FontAwesomeIcon icon={faChalkboard}/><span>{background_info.teacher_classroom ? background_info.teacher_classroom : 'No classroom set'}</span></div>
													<div className='mb-elem-homeroom-info-line'><FontAwesomeIcon icon={faUserGraduate}/> {background_info.number_homeroom_students} homeroom students</div>
												</div>
											}
											</>
											):(currentView === 'classroom') ?
											(
											<>
											<div className='mb-elem-homeroom-info mb-hide-during-print'>
												<div className='mb-elem-homeroom-info-line'><FontAwesomeIcon icon={faChalkboardTeacher}/><span>{background_info.homeroom_teacher_name ? background_info.homeroom_teacher_name : 'No teacher set'}</span></div>
											</div>
											</>
											):(currentView === 'student') ?
											(
											<>
											<div className='mb-elem-homeroom-info mb-hide-during-print'>
												{background_info.grade &&
													<div className='mb-elem-homeroom-info-line'><FontAwesomeIcon icon={faGraduationCap}/>{background_info.grade ? `Grade ${background_info.grade}` : 'No grade level set'}</div>
												}
												<div className='mb-elem-homeroom-info-line'><FontAwesomeIcon icon={faChalkboardTeacher}/><span>{background_info.homeroom_teacher_name ? background_info.homeroom_teacher_name : 'No homeroom teacher set'}</span></div>
											</div>
											</>
											):null}
											<FontAwesomeIcon icon={faEdit} className='gray-link mb-elem-edit-teacher-info mb-hide-during-print' onClick={() => toggleEditData(data_id)}/>
										</div>
										<div className='text-align-right mb-hide-during-print' onClick={() => handleToggleOpenDisplayData(data_id)}>
											<FontAwesomeIcon icon={data_open ? faChevronUp : faChevronDown} />
										</div>
									</div>
									<div className={`mb-elem-conflicts-link ${data_conflicts.length > 0 ? 'red' : 'green'}-link underline-dashed`} onClick={() => toggleOpenConflicts(data_id)}>{data_conflicts.length} conflicts/warnings</div>
									{openConflicts.includes(data_id) &&
										<div>
										{data_conflicts.map((conflict, conflict_index) => {
											return (
												<div className='mb-elem-conflict red-text' key={`${data_id}-${conflict_index}`}>
													<FontAwesomeIcon icon={faCircleExclamation} />
													<div>{conflict.display}</div>
												</div>
											)
										})}
										</div>
									}
									{data_open &&
										<>
										{blocksLoading ? (
											<div className='mb-elem-grid-loading-state'>
												<img src={require('../../images/balls.gif')} alt='loading blocks...' />
											</div>
										) : (
											<ScheduleGrid
												getDayKey={(day_number) => `${data_id}-${day_number}`}
												days={days}
												getDayLabel={(day_number) => `Day ${day_number}`}
												getDayItems={(day_number) => getDisplayDaySchedule(data_id, day_number, background_info)}
												getDayFloatingItems={(day_number) => getDisplayDayFloatingBlocks(data_id, day_number, background_info)}
												getDayOverlays={(day_number) => getDisplayDayOverlays(data_id, day_number)}
												onDayBodyClick={(currentView === 'teacher' && !blockData) ? (day_number, { clickTimeRange }) => createNewBlock('block', data_id, day_number, clickTimeRange) : null}
												renderOverlay={(overlay, { overlayIndex }) => (
													<ScheduleGridOverlayItem
														overlayKey={overlay.overlay_id || `${data_id}-${overlayIndex}`}
														rowStart={overlay.row_start}
														rowEnd={overlay.row_end}
														colStart={overlay.col_start || 1}
														colEnd={overlay.col_end || -1}
														className={overlay.className || ''}
														innerClassName={overlay.innerClassName || ''}
														content={overlay.content || null}
													/>
												)}
												renderItem={(block, { day, dayItems, loggedPositions }) => {
												const dayConflictKeys = getDisplayDayConflictKeys(data_id, day, background_info);
												const block_id = block.block_id;
												const block_delete_key = `${block_id}-${block.block_day_id || block.day_number}`;
												const block_deleting = deletingBlockTargets.includes(block_delete_key);
												const is_locked = (currentView !== 'student') ? (block.locked === '1') : display_info.student_locked_blocks.includes(block_id);
												const block_name_formatted = formatScheduleBlockName(block, data_id);

												const start_time = block.start_time;
												const end_time = block.end_time;
												const start_time_formatted = formatTime(start_time);
												const end_time_date_formatted = formatTime(end_time);
												const row_start = block.row_start;
												const row_end = block.row_end;
												const block_color = getScheduleBlockColor(block);
												const block_position = calculateTimeGridColumnStartEnd(block, dayItems, loggedPositions, start_time, end_time, 'block_id');
												const position_index = block_position.position_index;
												const col_start = block_position.col_start;
												const col_end = block_position.col_end;

												loggedPositions[block_id] = position_index;

													if(!row_start || !row_end || !col_start || !col_end) return null;

														return (
															<ScheduleGridItem
																key={`${block_id}-${block.block_day_id || block.day_number || day}`}
															itemKey={block_id}
															position={{ col_start, col_end }}
															rowStart={row_start}
															rowEnd={row_end}
															color={block_color}
															temporary={!!block.new}
															conflict={dayConflictKeys.has(`${block.block_day_id || block.block_id}-${day}`)}
															busy={block_deleting}
															busyContent={<img src={require('../../images/balls.gif')} alt='Deleting...' className='schedule-grid-item-loader' />}
															selected={blockData && blockData.block_id === block_id}
															onClick={(!blockData && currentView !== 'student' && !block_deleting) ? () => toggleEditBlock(block_id) : null}
																name={block_name_formatted}
																nameTooltipText={block_name_formatted}
																time={`${start_time_formatted} - ${end_time_date_formatted}`}
																timeTooltipText={`${start_time_formatted} - ${end_time_date_formatted}`}
																headerMenuOptions={(currentView === 'student' || block_deleting || !Array.isArray(block.block_days) || block.block_days.length <= 1) ? [] : [
																	{ label:'Remove This Day', onClick:(e) => removeBlock(block_id, e, { scope:'day', dayNumber:block.day_number }) },
																	{ label:'Remove All Days', onClick:(e) => removeBlock(block_id, e, { scope:'all' }) }
																]}
																headerAction={(currentView === 'student' && !block_deleting) ? <FontAwesomeIcon className='mb-elem-remove-block small-text cursor-pointer' icon={faTimes} onClick={(e) => removeStudentBlock(data_id, block_id, e)}/> : ((!block_deleting && (!Array.isArray(block.block_days) || block.block_days.length <= 1)) ? <FontAwesomeIcon className='mb-elem-remove-block small-text cursor-pointer' icon={faTimes} onClick={(e) => removeBlock(block_id, e, { scope:'all' })}/> : null)}
																data-blockid={block_id}
															>
															<div className='elem-lock-block-btn'>
																{(is_locked) ?
																(
																	<FontAwesomeIcon className='cursor-pointer bright-yellow-text' icon={faLock} onClick={(currentView === 'student') ? ()=>toggleLockStudentBlock(data_id, block_id, '0') : ()=>toggleLockBlock(block_id, '0')}/>
																):
																(
																	<FontAwesomeIcon className='opaque-link dark-gray-text' icon={faUnlock} onClick={(currentView === 'student') ? ()=>toggleLockStudentBlock(data_id, block_id, '1') : () => toggleLockBlock(block_id, '1')}/>
																)}
															</div>
														</ScheduleGridItem>
													);
												}}
												renderFloatingItem={(block, { day, dayItems, dayFloatingItems, loggedPositions }) => {
												const dayConflictKeys = getDisplayDayConflictKeys(data_id, day, background_info);
												const block_id = block.block_id;
												const block_name_formatted = formatScheduleBlockName(block, data_id);
												const start_time = block.start_time;
												const end_time = block.end_time;
												const start_time_formatted = formatTime(start_time);
												const end_time_date_formatted = formatTime(end_time);
												const row_start = block.row_start;
												const row_end = block.row_end;
												const block_color = getScheduleBlockColor(block);
												const block_position = calculateTimeGridColumnStartEnd(block, [...dayItems, ...dayFloatingItems], loggedPositions, start_time, end_time, 'block_id');
												const position_index = block_position.position_index;
												const col_start = block_position.col_start;
												const col_end = block_position.col_end;

												loggedPositions[block_id] = position_index;

													if(!row_start || !row_end || !col_start || !col_end) return null;

													return (
														<ScheduleGridItem
															key={`${block_id}-${block.block_day_id || block.day_number || day}-floating`}
															itemKey={`${block_id}-floating`}
															position={{ col_start, col_end }}
															rowStart={row_start}
															rowEnd={row_end}
															color={block_color}
															temporary={true}
															floating={true}
															conflict={dayConflictKeys.has(`${block.block_day_id || block.block_id}-${day}`)}
															selected={blockData && blockData.block_id === block_id}
															name={block_name_formatted}
															nameTooltipText={block_name_formatted}
															time={`${start_time_formatted} - ${end_time_date_formatted}`}
															timeTooltipText={`${start_time_formatted} - ${end_time_date_formatted}`}
														/>
													);
												}}
											renderDayFooter={(day_number) => (
												currentView === 'teacher' ?
													<div className='mb-add-new-section parent-div' data-teacherid={data_id} data-daynumber={day_number} key={`${data_id}-${day_number}`}>
														<div className='mb-elem-add-block-container mb-elem-add-block-container-subtle blue-link mb-hide-during-print' onClick={() => createNewBlock('block', data_id, day_number)}>
															<FontAwesomeIcon icon={faPlusCircle} />
															<div className='small-text'>Add Block</div>
														</div>
													</div>
												: null
											)}
										/>
										)}
										</>
									}
								</div>
							)
						})}	
						{(currentView === 'teacher' && teacher_counter > maxTeachersToShow) && 
							<div className='btn blue-btn load-more-btn' onClick={showMoreTeachers}>Load More Teachers</div>
						}
						{(currentView === 'student' && student_counter > maxStudentsToShow) && 
							<div className='btn blue-btn load-more-btn' onClick={showMoreStudents}>Load More Students</div>
						}
						</>
					)}			
				</div>
			</div>
		</DndProvider>
		</>
	)
}
